/**
 * Gets the appropriate logo URL for a school with fallback strategy
 * Priority:
 * 1. Theme-specific logo (logoUrlLight or logoUrlDark based on current theme)
 * 2. Legacy logos (logoThumbnailUrl or logoUrl)
 * 3. null
 *
 * @param school - School object containing logo URLs
 * @param theme - Current theme ('light', 'dark', 'system', or undefined)
 * @param preferThumbnail - Whether to prefer thumbnail over full logo in legacy fallback
 */
export function getSchoolLogoUrl(
  school: {
    logoUrlLight?: string | null
    logoUrlDark?: string | null
    logoThumbnailUrl?: string | null
    logoUrl?: string | null
  } | null,
  theme?: string | null,
  preferThumbnail: boolean = true
): string | null {
  if (!school) return null

  // Priority 1: Theme-specific logos
  if (theme === 'dark' && school.logoUrlDark) {
    return school.logoUrlDark
  }

  if (theme === 'light' && school.logoUrlLight) {
    return school.logoUrlLight
  }

  // Priority 2: Legacy fallback (for 'system' theme or when theme-specific logos don't exist)
  if (preferThumbnail) {
    return school.logoThumbnailUrl || school.logoUrl || null
  }

  return school.logoUrl || school.logoThumbnailUrl || null
}

/**
 * Gets school initials for avatar fallback
 * Returns first two letters of school name (e.g., "ST" for "Sample School")
 */
export function getSchoolInitials(schoolName: string | null | undefined): string {
  if (!schoolName) return 'SC'

  const words = schoolName.trim().split(/\s+/)
  if (words.length === 1) {
    return schoolName.substring(0, 2).toUpperCase()
  }

  return (words[0][0] + words[1][0]).toUpperCase()
}
